import { Link } from "react-router-dom";
import { ArrowRight, Volume2, VolumeX } from "lucide-react";
import { useState, useEffect, useRef } from "react";

const HERO_HEADLINE = (
  <>Deja de estar atrapado<br className="hidden lg:block" /> en la operación.<br className="hidden lg:block" /> <span className="text-[#FBE4D2]">Empieza a <span className="underline decoration-[#F26522] decoration-[3px] underline-offset-[6px]">dirigir tu empresa</span>.</span></>
);

/**
 * Hero — "Split Premium" (V2 elegida)
 * Foto reducida a ~70%, contenida con framing premium (rounded + ring),
 * texto domina el lado izquierdo.
 */
export default function Hero() {
  const [isMuted, setIsMuted] = useState(true);
  const [videoEnded, setVideoEnded] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const iframeRef = useRef(null);
  const playerRespondedRef = useRef(false);

  const toggleMute = () => {
    if (!iframeRef.current) return;
    const func = isMuted ? "unMute" : "mute";
    iframeRef.current.contentWindow.postMessage(
      JSON.stringify({ event: "command", func, args: "" }),
      "*"
    );
    setIsMuted(!isMuted);
  };

  // Detect video end / errors via YouTube postMessage API (enablejsapi=1).
  // If the player never confirms it's ready/playing (blocked embed, ad-blocker,
  // network restriction), fall back to the photo instead of leaving YouTube's
  // native error box on screen.
  useEffect(() => {
    const onMessage = (e) => {
      if (!e.data || typeof e.data !== "string") return;
      try {
        const msg = JSON.parse(e.data);
        if (msg.event === "onReady") {
          playerRespondedRef.current = true;
        }
        if (msg.event === "onError") {
          setVideoFailed(true);
        }
        if (msg.event === "onStateChange") {
          playerRespondedRef.current = true;
          if (msg.info === 0) setVideoEnded(true);
        }
      } catch (_) {}
    };
    window.addEventListener("message", onMessage);

    const failSafeTimer = setTimeout(() => {
      if (!playerRespondedRef.current) setVideoFailed(true);
    }, 6000);

    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(failSafeTimer);
    };
  }, []);

  return (
    <section id="top" data-testid="hero-section" className="relative bg-[#0A1128] text-white overflow-hidden">
      {/* Atmospheric */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#F26522] opacity-[0.10] blur-[120px]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage: "linear-gradient(to bottom, black, transparent 95%)",
            WebkitMaskImage: "linear-gradient(to bottom, black, transparent 95%)",
          }}
        />
      </div>

      <div className="grid lg:grid-cols-12 min-h-[calc(100vh-72px)] lg:min-h-[580px] lg:max-h-[860px] relative">
        {/* LEFT — text */}
        <div className="lg:col-span-7 xl:col-span-8 flex flex-col justify-center pt-24 lg:pt-24 pb-12 lg:pb-16 px-5 sm:px-8 lg:pl-[max(2rem,calc((100vw-1280px)/2+2rem))] lg:pr-12">
          <p className="text-[10px] sm:text-xs font-bold tracking-[0.12em] sm:tracking-[0.28em] uppercase text-[#F26522]">
            EOS Professional Implementer<sup className="text-[7px] ml-0.5">®</sup> · Chile y Latinoamérica
          </p>

          {/* Headline anterior — comentado para recuperar si es necesario */}
          {/* <h1 className="mt-5 lg:mt-6 font-display font-extrabold text-white leading-[1.05] tracking-[-0.025em] text-3xl sm:text-4xl lg:text-5xl xl:text-[60px]">
            Lleva tu negocio al siguiente nivel,{" "}
            <span className="text-[#FBE4D2]">
              como{" "}
              <span className="underline decoration-[#F26522] decoration-[3px] underline-offset-[6px]">
                miles de empresas exitosas
              </span>{" "}
              en el mundo con{" "}
            </span>
            <span className="text-[#F26522]">EOS</span>
          </h1> */}

          <h1 className="mt-5 lg:mt-6 font-display font-extrabold text-white leading-[1.05] tracking-[-0.025em] text-3xl sm:text-4xl lg:text-5xl xl:text-[60px]">
            {HERO_HEADLINE}
          </h1>

          <p className="mt-5 lg:mt-6 max-w-xl text-base sm:text-lg lg:text-lg text-slate-300 leading-relaxed">
            EOS ayuda a tu equipo de liderazgo a alinearse, ejecutar con disciplina y construir una empresa que funcione mejor sin depender de ti para todo.
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link
              to="/contacto"
              data-testid="hero-primary-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#F26522] text-white font-bold uppercase tracking-[0.08em] text-sm px-6 py-3.5 rounded-md shadow-lg shadow-[#F26522]/20 transition-all hover:bg-[#D95214] hover:-translate-y-0.5"
            >
              Agendar una llamada de 15 minutos
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#que-es-eos"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-bold uppercase tracking-[0.08em] text-sm px-6 py-3.5 rounded-md hover:bg-white/5 hover:border-white/60 transition-all"
            >
              ¿Qué es EOS?
            </a>
          </div>
          <p data-testid="hero-cta-note" className="mt-3 text-sm text-slate-400">Sin costo. Sin compromiso.</p>
        </div>

        {/* RIGHT — video column (YouTube Short) with photo fallback */}
        <div className="lg:col-span-5 xl:col-span-4 relative flex items-center justify-center px-6 lg:px-0 lg:pr-8 xl:pr-12 py-10 lg:py-8">
          <div className="relative w-full max-w-[260px] sm:max-w-[300px] lg:max-w-[270px] xl:max-w-[300px] aspect-[9/16]">
            {/* Frame container */}
            <div className="absolute inset-0 rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.6)]">
              {/* Fallback photo (visible if video fails/blocked) */}
              <img
                src="/carlos-perfil.png"
                alt="Carlos Garcín — Professional EOS Implementer"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              {/* YouTube Short — plays once, fades to photo on end or on playback failure */}
              <iframe
                ref={iframeRef}
                className="absolute inset-0 w-full h-full transition-opacity duration-700"
                style={{ opacity: (videoEnded || videoFailed) ? 0 : 1, pointerEvents: (videoEnded || videoFailed) ? "none" : "auto" }}
                src="https://www.youtube.com/embed/EYwWG_I8v0I?autoplay=1&mute=1&controls=0&rel=0&playsinline=1&cc_load_policy=0&enablejsapi=1"
                title="Carlos Garcín — EOS Implementer en acción"
                frameBorder="0"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
              {/* Gradient overlay for badge readability */}
              <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#0A1128]/70 to-transparent pointer-events-none" />
            </div>

            {/* EOS Implementer badge */}
            <div className="absolute bottom-3 right-3 lg:-right-5 w-16 lg:w-20 drop-shadow-2xl z-10">
              <img
                src="/eos-implementer-badge.png"
                alt="Certified Professional EOS Implementer"
                className="w-full h-auto"
              />
            </div>

            {/* Custom mute/unmute button — hidden once video ends or fails */}
            {!videoEnded && !videoFailed && (
              <button
                onClick={toggleMute}
                data-testid="hero-video-mute-toggle"
                aria-label={isMuted ? "Activar sonido" : "Silenciar video"}
                className="absolute bottom-4 left-4 z-10 flex items-center gap-1.5 bg-black/50 hover:bg-black/70 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full backdrop-blur-sm transition-all"
              >
                {isMuted ? (
                  <VolumeX className="w-3.5 h-3.5" />
                ) : (
                  <Volume2 className="w-3.5 h-3.5" />
                )}
                {isMuted ? "Sonido" : "Silenciar"}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
